import { Sidebar } from './sidebar';

import * as React from 'react';

import { Store } from "lucide-react";

import { Header } from './header';
import { Outlet, ScrollRestoration, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/use-auth';
import { useMembershipsSuspense } from '@/features/memberships/api/list-memberships';
import { HamburgerButton } from './header/hamburger-button';
import { UserMenu } from '@/features/users/components/user-menu';
import { Membership, MembershipRole } from '@/types/api/membership';
import { useUserSuspense } from '@/features/users/api/get-user';
import { membershipRoleLabel } from '@/features/memberships/utils/membership-role';
import { useRevokeSessionMutation } from '@/hooks/auth/mutations/use-revoke-session.mutation';
import { useSwitchAuthContextMutation } from '@/hooks/auth/mutations/use-switch-auth-context.mutation';
import { HelpMenu } from '../../help-menu';
import { useSalesSuspense } from '@/features/sales/api/list-sales';
import { SaleSelect } from '@/features/sales/components/sale-select';
import { Sale, SaleStatus } from '@/types/api/sale';
import { paths } from '@/config/paths';
import { LookupCreateShop } from "@/features/shops/components/lookup-create-shop";
import SellFab from '@/components/sell-fab';
import { Loader } from '@/shared/components/ui/loader';

const membershipToContext = (m: Membership, currentGroupId: string) => ({
  name: m.group!.name,
  id: m.groupId,
  active: m.groupId === currentGroupId,
});

const saleToOption = (s: Sale) => ({
  label: s.name,
  value: s.id,
});

export function DashboardLayout() {
  const { context } = useAuth();

  return (
    <React.Suspense fallback={<div className="grid min-h-dvh place-items-center"><Loader /></div>}>
      <DashboardLayoutContent key={context!.groupId} />
    </React.Suspense>
  );
}

function DashboardLayoutContent() {
  const { context } = useAuth();
  const { saleId } = useParams();
  const navigate = useNavigate();
  const userId = context!.userId;
  const groupId = context!.groupId;
  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(false);
  const { data: user } = useUserSuspense({ userId });
  const { data: memberships } = useMembershipsSuspense({ params: { include: ['group'], userId } });
  const { data: sales } = useSalesSuspense({ params: { groupId } });
  const { mutate: logout } = useRevokeSessionMutation();
  const { mutate: switchContext } = useSwitchAuthContextMutation();

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/*  Site header */}
        <Header>
          {/* Header: Left side */}
          <div className="flex gap-4">
            <HamburgerButton sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            {saleId && (
              <SaleSelect
                value={saleId}
                onChange={(saleId) => navigate(paths.sale.overview.getHref(saleId))}
                options={sales.map(saleToOption)}
                showCreateSale={context!.membershipRoles.includes(MembershipRole.Leader)}
              />
            )}
          </div>

          {/* Header: Right side */}
          <div className="flex items-center space-x-2 overflow-hidden ml-4 relative">
            <HelpMenu />
            <UserMenu
              contexts={memberships.map((m) => membershipToContext(m, groupId))}
              onContextSwitch={switchContext}
              onLogout={logout}
              label={user.name}
              sublabel={context!.membershipRoles.map(membershipRoleLabel).join(', ')}
            />
          </div>
        </Header>

        <main className="grow">
          <div className="relative px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto h-full">
            <Outlet />
            
            {saleId && 
              <LookupCreateShop saleId={saleId} className="fixed right-4 bottom-4 p-6 py-8 md:p-8 rounded-full text-md md:text-lg">
                <Store className="size-6 md:size-8 " />
                Webshop
              </LookupCreateShop>
            }
          </div>
        </main>
      </div>
      <ScrollRestoration />
    </div>
  );
}
